import { useState } from 'react';
import { Loader2, Grid3X3 } from 'lucide-react';
import { format, addDays, parseISO } from 'date-fns';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/lib/error';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState } from '@/components/ui/EmptyState';
import { SlideOver } from '@/components/ui/SlideOver';
import { useHouseInventory } from '@/hooks/api/useHouseInventory';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import type {
  HouseInventoryGrid,
  HouseInventoryCell,
} from '@/services/api/frontdeskOps.types';

// ─── Page ────────────────────────────────────────────────────────────────────

export function HouseInventoryPage() {
  const propertyId = useSelectedPropertyId();
  const [fromDate, setFromDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [toDate, setToDate] = useState(() => format(addDays(new Date(), 13), 'yyyy-MM-dd'));
  // OOO drill: the cell whose maintenance request ids we're inspecting.
  const [drillCell, setDrillCell] = useState<HouseInventoryCell | null>(null);

  const rangeValid = !!fromDate && !!toDate && fromDate <= toDate;

  const { data, isLoading, isError, error } = useHouseInventory(
    { propertyId, from: fromDate, to: toDate },
    !!propertyId && rangeValid,
  );

  return (
    <div>
      <PageHeader
        title="House Inventory"
        description="Total, available, reserved and out-of-order by room type and date"
        actions={
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="From date"
            />
            <span className="text-gray-400 text-sm">to</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="To date"
            />
          </div>
        }
      />

      {!rangeValid && (
        <p className="text-sm text-amber-600 mb-4" role="alert">
          End date must be on or after start date.
        </p>
      )}

      {!propertyId ? (
        <EmptyState icon={Grid3X3} title="Select a property" description="Select a property to continue." />
      ) : !rangeValid ? (
        <EmptyState icon={Grid3X3} title="Adjust the date range" description="End date must be on or after start date." />
      ) : isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-primary-500" aria-label="Loading" />
        </div>
      ) : isError ? (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(error)}
        </p>
      ) : !data || data.roomTypes.length === 0 ? (
        <EmptyState icon={Grid3X3} title="No inventory data" description="No room types found for this property and date range." />
      ) : (
        <InventoryGrid data={data} onDrillOoo={setDrillCell} />
      )}

      {/* OOO drill-down */}
      <SlideOver
        isOpen={drillCell !== null}
        onClose={() => setDrillCell(null)}
        title="Out-of-Order Rooms"
      >
        {drillCell && <OooDrill cell={drillCell} />}
      </SlideOver>
    </div>
  );
}

// ─── Grid ────────────────────────────────────────────────────────────────────

function InventoryGrid({
  data,
  onDrillOoo,
}: {
  data: HouseInventoryGrid;
  onDrillOoo: (cell: HouseInventoryCell) => void;
}) {
  const dates = data.dates;

  // Per-date column totals for the bottom summary row.
  const totalsByDate = dates.map((d) => {
    let total = 0;
    let available = 0;
    let reserved = 0;
    let ooo = 0;
    for (const rt of data.roomTypes) {
      const cell = rt.dates.find((c) => c.date === d);
      if (cell) {
        total += cell.total;
        available += cell.available;
        reserved += cell.reserved;
        ooo += cell.ooo;
      }
    }
    return { date: d, total, available, reserved, ooo };
  });

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="bg-gray-50">
              <th className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[180px] border-r border-gray-200">
                Room Type
              </th>
              {dates.map((d) => {
                let label: string;
                try {
                  label = format(parseISO(d), 'EEE MMM d');
                } catch {
                  label = d;
                }
                return (
                  <th
                    key={d}
                    scope="col"
                    className="px-3 py-3 text-center text-xs font-semibold text-gray-500 min-w-[110px] border-r border-gray-100"
                  >
                    {label}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {data.roomTypes.map((rt) => (
              <tr key={rt.id} className="hover:bg-gray-50/50">
                <td className="sticky left-0 z-10 bg-white px-4 py-3 border-r border-gray-200">
                  <div className="font-medium text-sm text-gray-900">{rt.name}</div>
                  {rt.code && <div className="text-xs text-gray-400">{rt.code}</div>}
                </td>
                {dates.map((d) => {
                  const cell = rt.dates.find((c) => c.date === d);
                  return (
                    <td key={d} className="px-3 py-2 text-center border-r border-gray-100">
                      {cell ? <InventoryCellView cell={cell} onDrillOoo={onDrillOoo} /> : <span className="text-gray-300">--</span>}
                    </td>
                  );
                })}
              </tr>
            ))}

            {/* Total summary row */}
            <tr className="bg-gray-50 font-semibold">
              <td className="sticky left-0 z-10 bg-gray-50 px-4 py-3 border-r border-gray-200 text-sm text-gray-700">
                Total
              </td>
              {totalsByDate.map((c) => (
                <td key={c.date} className="px-3 py-2 text-center border-r border-gray-100">
                  <InventoryCellView cell={c} onDrillOoo={onDrillOoo} readOnly />
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="border-t border-gray-200 px-4 py-3 flex items-center gap-4 text-xs text-gray-500 flex-wrap">
        <span>T = Total</span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-green-100 border border-green-300" aria-hidden="true" />
          A = Available
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-blue-100 border border-blue-300" aria-hidden="true" />
          R = Reserved
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-red-100 border border-red-300" aria-hidden="true" />
          OOO = Out of Order
        </span>
      </div>
    </div>
  );
}

// ─── Cell ────────────────────────────────────────────────────────────────────

function InventoryCellView({
  cell,
  onDrillOoo,
  readOnly = false,
}: {
  cell: { total: number; available: number; reserved: number; ooo: number; maintenanceRequestIds?: string[] };
  onDrillOoo: (cell: HouseInventoryCell) => void;
  readOnly?: boolean;
}) {
  const ratio = cell.total > 0 ? cell.available / cell.total : 0;
  const availClass =
    ratio > 0.5
      ? 'bg-green-50 text-green-700'
      : ratio > 0.2
        ? 'bg-yellow-50 text-yellow-700'
        : 'bg-red-50 text-red-700';

  const oooDrillable = !readOnly && cell.ooo > 0;

  return (
    <div className="flex flex-col items-center gap-0.5 text-[11px]">
      <span className={cn('inline-flex items-center rounded px-2 py-0.5 text-xs font-bold', availClass)}>
        {cell.available}
      </span>
      <div className="flex items-center gap-1 text-gray-400">
        <span title="Total">T{cell.total}</span>
        <span className="text-blue-500" title="Reserved">R{cell.reserved}</span>
        {cell.ooo > 0 ? (
          oooDrillable ? (
            <button
              type="button"
              onClick={() => onDrillOoo(cell as HouseInventoryCell)}
              className="text-red-600 font-semibold underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 rounded"
              title="View out-of-order maintenance requests"
            >
              OOO{cell.ooo}
            </button>
          ) : (
            <span className="text-red-600 font-semibold" title="Out of Order">OOO{cell.ooo}</span>
          )
        ) : null}
      </div>
    </div>
  );
}

// ─── OOO drill-down ──────────────────────────────────────────────────────────

function OooDrill({ cell }: { cell: HouseInventoryCell }) {
  const ids = cell.maintenanceRequestIds ?? [];
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        {cell.ooo} out-of-order room{cell.ooo !== 1 ? 's' : ''} on{' '}
        <span className="font-medium text-gray-900">{cell.date}</span>, derived from active
        maintenance requests.
      </p>
      {ids.length === 0 ? (
        <p className="text-sm text-gray-400">No maintenance request details available.</p>
      ) : (
        <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
          {ids.map((id) => (
            <li key={id} className="px-3 py-2 flex items-center justify-between text-sm">
              <span className="font-mono text-gray-600">{id.slice(0, 8)}</span>
              <a
                href="/maintenance"
                className="text-primary-600 hover:underline text-xs"
              >
                View in Maintenance
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
